import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

// ─── Toast Notification ──────────────────────────────────────────────────────
// Identico al DettaglioModal — animato, auto-dismiss
const Toast = ({ notification }) => {
  if (!notification) return null;
  const isSuccess = notification.type === 'success';
  const isWarn    = notification.type === 'warn';

  const bg = isSuccess ? 'bg-emerald-500' : isWarn ? 'bg-amber-500' : 'bg-red-500';
  const iconName = isSuccess ? 'CheckCircle' : isWarn ? 'AlertTriangle' : 'AlertCircle';

  return (
    <div className={`fixed top-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 ${bg} text-white`}>
      <Icon name={iconName} size={20} />
      <span className="text-sm font-black uppercase tracking-wider whitespace-nowrap">{notification.message}</span>
    </div>
  );
};

// ─── Custom Confirm Dialog ────────────────────────────────────────────────────
// Sostituisce window.confirm — stesso stile del resto del modal
const ConfirmDialog = ({ open, message, sublabel, onConfirm, onCancel, confirmLabel = 'Conferma', confirmColor = 'bg-red-500 hover:bg-red-600 text-white border-red-600' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-card border border-border/60 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/50 to-transparent" />
        <div className="p-7 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-base font-black uppercase tracking-tight text-foreground">{message}</span>
            {sublabel && <span className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">{sublabel}</span>}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 rounded-2xl bg-muted hover:bg-muted/80 border border-border/40 text-foreground text-[11px] font-black uppercase tracking-widest transition-all"
            >
              Annulla
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 py-2.5 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${confirmColor}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Campo form moderno ──────────────────────────────────────────────────────
const ModernField = ({
  label, name, formData, onChange, options = [],
  type = 'text', placeholder, error, required,
  onBlur, suffix, disabled = false,
}) => {
  const hasValue = formData[name] !== '' && formData[name] != null;
  return (
    <div className="flex flex-col gap-1.5 w-full group">
      {/* Label row */}
      <div className="flex justify-between items-center px-1">
        <label className={`text-[10px] font-black uppercase tracking-[.15em] transition-colors select-none ${
          error ? 'text-red-500' : 'text-muted-foreground/60 group-focus-within:text-primary'
        }`}>
          {label}{required && <span className="text-primary ml-0.5">•</span>}
        </label>
        {error && (
          <span className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1 animate-in fade-in duration-200">
            <Icon name="AlertCircle" size={9} />
            {error}
          </span>
        )}
      </div>

      {/* Input */}
      <div className="relative">
        {options.length > 0 && (
          <datalist id={`${name}-list`}>
            {options.map(opt => <option key={opt} value={opt} />)}
          </datalist>
        )}
        <input
          type={type}
          name={name}
          list={options.length > 0 ? `${name}-list` : undefined}
          value={formData[name] ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all
            ${suffix ? 'pr-10' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed bg-muted/20 border border-border/30' : ''}
            ${error
              ? 'bg-red-500/5 border-2 border-red-500/40 focus:border-red-500/60 focus:ring-2 focus:ring-red-500/10'
              : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
            }
          `}
        />
        {suffix && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground/30 pointer-events-none uppercase">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Sezione titoletto ────────────────────────────────────────────────────────
const SectionTitle = ({ label, icon }) => (
  <div className="flex items-center gap-3 md:col-span-3 mt-2">
    {icon && <Icon name={icon} size={13} className="text-primary/60" />}
    <span className="text-[10px] font-black uppercase tracking-[.25em] text-primary/70">{label}</span>
    <div className="flex-1 h-px bg-border/30" />
  </div>
);

// ─── Componente principale ────────────────────────────────────────────────────
const NewInsertCCO = ({ isOpen, onClose, onRefresh }) => {

  const emptyForm = {
    ean: '', nome: '', reparto: '', mm: '', stato: '',
    minscorta: '', qtapresente: '', dataordine: '',
    datasostituzione: '2010-01-01', sap: '', note: '',
  };

  const [formData, setFormData]             = useState(emptyForm);
  const [options, setOptions]               = useState({ reparto: [], mm: [], stato: [], minscorta: [] });
  const [errors, setErrors]                 = useState({});
  const [isLoading, setIsLoading]           = useState(false);
  const [isCheckingEan, setIsCheckingEan]   = useState(false);
  const [notification, setNotification]     = useState(null);
  const [confirmClose, setConfirmClose]     = useState(false); // confirm dialog stato

  // ── Auto-dismiss toast ────────────────────────────────────────────────────
  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 4500);
    return () => clearTimeout(t);
  }, [notification]);

  // ── Blocca scroll body ────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchDistinctOptions();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // ── Escape key ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, formData]);

  // ── Fetch opzioni autocomplete dalla tabella ──────────────────────────────
  const fetchDistinctOptions = async () => {
    try {
      const fields = ['reparto', 'mm', 'stato', 'minscorta'];
      const newOptions = {};
      for (const field of fields) {
        const { data } = await supabase.from('cco_magazzino').select(field).not(field, 'is', null);
        newOptions[field] = [...new Set(data.map(item => item[field]))].sort();
      }
      setOptions(newOptions);
    } catch (err) {
      console.error('Fetch options error:', err);
    }
  };

  // ── Check EAN duplicato al blur ────────────────────────────────────────────
  const checkEanExists = async (ean) => {
    if (!ean) return;
    setIsCheckingEan(true);
    try {
      const { data } = await supabase
        .from('cco_magazzino')
        .select('ean')
        .eq('ean', ean)
        .maybeSingle();

      if (data) {
        setErrors(prev => ({ ...prev, ean: 'EAN già esistente!' }));
        setNotification({ type: 'error', message: `EAN ${ean} già presente nel magazzino!` });
      } else {
        setErrors(prev => ({ ...prev, ean: null }));
      }
    } catch (err) {
      console.error('EAN check error:', err);
    } finally {
      setIsCheckingEan(false);
    }
  };

  // ── Input change ──────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // ── Validazione ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    const required = ['ean', 'nome', 'reparto', 'mm', 'stato', 'minscorta', 'qtapresente'];
    required.forEach(field => {
      if (!formData[field] || String(formData[field]).trim() === '') {
        newErrors[field] = 'Obbligatorio';
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setNotification({ type: 'error', message: 'Compila tutti i campi obbligatori!' });
    }
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm() || errors.ean) {
      if (errors.ean) setNotification({ type: 'error', message: 'Correggi l\'EAN prima di procedere!' });
      return;
    }

    setIsLoading(true);
    try {
      const insertData = {
        ...formData,
        ean:          parseInt(formData.ean),
        minscorta:    parseInt(formData.minscorta),
        qtapresente:  parseInt(formData.qtapresente),
        note:         formData.note.trim() || 'Senza note',
        dataordine:   formData.dataordine || null,
      };

      const { error } = await supabase.from('cco_magazzino').insert([insertData]);
      if (error) throw error;

      // Success toast, poi chiude dopo un momento
      setNotification({ type: 'success', message: `Articolo "${formData.nome}" inserito con successo!` });
      onRefresh?.();
      setTimeout(() => {
        resetAndClose();
      }, 4000);

    } catch (err) {
      setNotification({ type: 'error', message: 'Errore durante l\'inserimento: ' + err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset form e chiudi ───────────────────────────────────────────────────
  const resetAndClose = () => {
    setFormData(emptyForm);
    setErrors({});
    setNotification(null);
    onClose();
  };

  // ── Chiudi con confirm se ci sono dati inseriti ────────────────────────────
  const handleClose = () => {
    const isDirty = Object.entries(formData).some(([k, v]) => {
      // ignora datasostituzione che ha un default
      if (k === 'datasostituzione') return false;
      return v !== '' && v != null;
    });

    if (isDirty) {
      setConfirmClose(true); // mostra il custom confirm dialog
    } else {
      resetAndClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* ── Toast globale (sopra tutto) ── */}
      <Toast notification={notification} />

      {/* ── Confirm Dialog chiusura ── */}
      <ConfirmDialog
        open={confirmClose}
        message="Scartare le modifiche?"
        sublabel="I dati inseriti andranno persi"
        confirmLabel="Esci senza salvare"
        confirmColor="bg-red-500 hover:bg-red-600 text-white border-red-500"
        onConfirm={() => { setConfirmClose(false); resetAndClose(); }}
        onCancel={() => setConfirmClose(false)}
      />

      {/* ── Overlay + Modal ── */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-hidden">

        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
          onClick={handleClose}
        />

        {/* Pannello */}
        <div className="relative w-full max-w-4xl bg-card border border-border/50 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-300">

          {/* Glow decorativo */}
          <div className="absolute -top-20 -right-20 w-56 h-56 bg-primary/10 blur-[70px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-56 h-56 bg-primary/5  blur-[70px] rounded-full pointer-events-none" />

          {/* ── HEADER ── */}
          <div className="shrink-0 px-8 md:px-10 py-7 flex items-center justify-between border-b border-border/40 relative z-10 bg-card/60 backdrop-blur-sm">
            <div className="flex items-center gap-5">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-sm">
                <Icon name="PlusCircle" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-black tracking-tighter uppercase text-foreground">Nuovo Articolo</h2>
                <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mt-0.5">
                  Inserimento magazzino CCO
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center rounded-2xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-muted-foreground transition-all duration-200 group"
            >
              <Icon name="X" size={18} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>

          {/* ── FORM BODY ── */}
          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto px-8 md:px-10 py-8 relative z-10"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">

              {/* ── Sezione: Identificazione ── */}
              <SectionTitle label="Identificazione" icon="Fingerprint" />

              {/* EAN */}
              <div className="relative">
                <ModernField
                  label="EAN Code"
                  name="ean"
                  formData={formData}
                  onChange={handleChange}
                  onBlur={(e) => checkEanExists(e.target.value)}
                  type="number"
                  placeholder="202410xxx"
                  error={errors.ean}
                  required
                />
                {isCheckingEan && (
                  <div className="absolute right-3.5 bottom-3.5">
                    <Icon name="Loader2" size={14} className="animate-spin text-primary/60" />
                  </div>
                )}
                {!isCheckingEan && formData.ean && !errors.ean && (
                  <div className="absolute right-3.5 bottom-3.5">
                    <Icon name="CheckCircle2" size={14} className="text-emerald-500" />
                  </div>
                )}
              </div>

              {/* Nome — occupa 2 colonne */}
              <div className="md:col-span-2">
                <ModernField
                  label="Nome Articolo"
                  name="nome"
                  formData={formData}
                  onChange={handleChange}
                  placeholder="Es. Motore, Valvola, Ruota..."
                  error={errors.nome}
                  required
                />
              </div>

              {/* ── Sezione: Logistica & Stato ── */}
              <SectionTitle label="Logistica & Stato" icon="Box" />

              <ModernField
                label="Reparto"
                name="reparto"
                formData={formData}
                onChange={handleChange}
                options={options.reparto}
                placeholder="Scegli o digita..."
                error={errors.reparto}
                required
              />
              <ModernField
                label="Misura (MM)"
                name="mm"
                formData={formData}
                onChange={handleChange}
                options={options.mm}
                placeholder="Es. 10"
                error={errors.mm}
                suffix="mm"
                required
              />
              <ModernField
                label="Stato Articolo"
                name="stato"
                formData={formData}
                onChange={handleChange}
                options={options.stato}
                placeholder="Pronto / In Ordine..."
                error={errors.stato}
                required
              />

              {/* Quantità e scorta min sulla stessa riga */}
              <ModernField
                label="Quantità Presente"
                name="qtapresente"
                formData={formData}
                onChange={handleChange}
                type="number"
                placeholder="0"
                suffix="pz"
                error={errors.qtapresente}
                required
              />
              <ModernField
                label="Scorta Minima"
                name="minscorta"
                formData={formData}
                onChange={handleChange}
                options={options.minscorta}
                type="number"
                placeholder="0"
                suffix="pz"
                error={errors.minscorta}
                required
              />
              <ModernField
                label="Codice SAP"
                name="sap"
                formData={formData}
                onChange={handleChange}
                placeholder="Opzionale"
              />

              {/* ── Sezione: Cronologia & Dettagli ── */}
              <SectionTitle label="Cronologia & Dettagli" icon="Calendar" />

              <ModernField
                label="Data Ordine"
                name="dataordine"
                formData={formData}
                onChange={handleChange}
                type="date"
              />
              <ModernField
                label="Data Sostituzione"
                name="datasostituzione"
                formData={formData}
                onChange={handleChange}
                type="date"
              />

              {/* Note — occupa intera riga */}
              <div className="md:col-span-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/60 px-1 select-none">
                    Note Extra
                  </label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Informazioni aggiuntive, note di ricezione, osservazioni..."
                    rows={3}
                    className="w-full bg-muted/30 border border-border/30 rounded-2xl px-4 py-3 text-sm font-bold outline-none focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60 transition-all resize-none"
                  />
                </div>
              </div>

            </div>
          </form>

          {/* ── FOOTER ── */}
          <div className="shrink-0 px-8 md:px-10 py-6 bg-muted/30 border-t border-border/40 flex flex-col sm:flex-row gap-3 relative z-10">
            {/* Info totale campi compilati */}
            <div className="hidden sm:flex items-center gap-2 text-muted-foreground/30 mr-auto">
              <Icon name="Info" size={12} />
              <span className="text-[9px] font-black uppercase tracking-[.2em]">
                {Object.values(formData).filter(v => v !== '' && v !== '2010-01-01' && v != null).length} campi compilati
              </span>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="flex-1 sm:flex-none sm:w-36 py-3.5 rounded-2xl bg-muted hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-foreground text-[11px] font-black uppercase tracking-[.2em] transition-all duration-200"
            >
              Annulla
            </button>

            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || isCheckingEan}
              className="flex-1 sm:flex-[2] py-3.5 rounded-2xl bg-primary text-primary-foreground text-[11px] font-black uppercase tracking-[.2em] shadow-lg shadow-primary/20 hover:opacity-90 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Inserimento...
                </>
              ) : (
                <>
                  <Icon name="PlusCircle" size={15} />
                  Conferma Inserimento
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default NewInsertCCO;